import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Slider } from "../components/ui/slider";
import { ShieldAlert, Flame, HeartPulse, AlertTriangle, TrafficCone, Radar, Volume2, Bell, Loader2 } from "lucide-react";
import { getPreferredTtsLang, setPreferredTtsLang, playSpokenAlert } from "../utils/alertSound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TTS_LANGS = [
  { code: "en-US", label: "English (US)" },
  { code: "en-GB", label: "English (UK)" },
  { code: "yo-NG", label: "Yorùbá" },
  { code: "ha-NG", label: "Hausa" },
  { code: "ig-NG", label: "Igbo" },
  { code: "pcm", label: "Naija Pidgin" },
];

const DEFAULT_RADIUS = { security: 10, fire: 10, medical: 10, accident: 10, banditry: 20, ecaution: 0.5 };

const alertTypes = [
  { key: "security", labelKey: "proximity.security", icon: ShieldAlert, color: "text-red-500", accent: "bg-red-500" },
  { key: "fire", labelKey: "proximity.fire", icon: Flame, color: "text-orange-500", accent: "bg-orange-500" },
  { key: "medical", labelKey: "proximity.medical", icon: HeartPulse, color: "text-blue-500", accent: "bg-blue-500" },
  { key: "accident", labelKey: "proximity.accident", icon: AlertTriangle, color: "text-yellow-600", accent: "bg-yellow-500" },
  { key: "banditry", labelKey: "proximity.banditry", icon: AlertTriangle, color: "text-rose-900", accent: "bg-rose-900" },
  { key: "ecaution", labelKey: "proximity.ecaution", icon: TrafficCone, color: "text-amber-600", accent: "bg-amber-600" },
];

export default function ProximitySettings({ radius, onUpdate }) {
  const { t } = useTranslation();
  const [local, setLocal] = useState(() => {
    if (typeof radius === "object" && radius !== null) return { ...DEFAULT_RADIUS, ...radius };
    if (typeof radius === "number") return { security: radius, fire: radius, medical: radius, accident: radius, banditry: radius, ecaution: 0.5 };
    return { ...DEFAULT_RADIUS };
  });
  const [ttsLang, setTtsLang] = useState(() => getPreferredTtsLang());
  // Push-notification self-test — a "Send test notification" button
  // below lets the resident (and support) confirm the full pipeline is
  // wired end-to-end: SW registration → PushManager subscription →
  // backend row → VAPID delivery → OS notification tray. Silent breaks
  // (dead endpoint, missing VAPID keys, corporate firewall blocking
  // Google/Mozilla Push service) are impossible to diagnose from the
  // client alone otherwise.
  const [pushTestBusy, setPushTestBusy] = useState(false);
  const pushPermission =
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "unsupported";

  const handleSendTestPush = async () => {
    if (pushTestBusy) return;
    setPushTestBusy(true);
    try {
      const r = await axios.post(`${API}/push/test`, {}, { withCredentials: true });
      const { sent, subscriptions, message } = r.data || {};
      if (sent) {
        toast.success("Test notification dispatched", {
          description:
            `${subscriptions} subscription(s). Check your device notification tray — it should arrive within a few seconds.`,
          duration: 8000,
        });
      } else {
        toast("No push subscription on file", {
          description:
            message ||
            "Tap 'Enable ring notifications' on your Scan2Call tile first, then try again.",
          duration: 8000,
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error(`Test push failed: ${detail}`);
    } finally {
      setPushTestBusy(false);
    }
  };

  useEffect(() => {
    if (typeof radius === "object" && radius !== null) {
      setLocal({ ...DEFAULT_RADIUS, ...radius });
    } else if (typeof radius === "number") {
      setLocal({ security: radius, fire: radius, medical: radius, accident: radius, banditry: radius, ecaution: 0.5 });
    }
  }, [radius]);

  const handleChange = (type, val) => {
    const next = { ...local, [type]: val[0] };
    setLocal(next);
  };

  const handleCommit = (type, val) => {
    const next = { ...local, [type]: val[0] };
    setLocal(next);
    onUpdate(next);
  };

  return (
    <div className="mt-5" data-testid="proximity-settings">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-3 flex items-center gap-1.5">
        <Radar className="w-3.5 h-3.5" /> {t("proximity.title")}
      </p>
      <div className="space-y-3">
        {alertTypes.map(({ key, labelKey, icon: Icon, color }) => {
          const isEcaution = key === "ecaution";
          const displayVal = isEcaution
            ? `${Math.round(local[key] * 1000)} m`
            : `${local[key]} km`;
          return (
            <div key={key} data-testid={`proximity-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <Icon className={`w-3.5 h-3.5 ${color}`} />
                  <span className="text-[11px] font-bold text-gray-700 font-body">{t(labelKey)}</span>
                </div>
                <span className="text-[11px] font-bold text-gray-900 font-heading" data-testid={`proximity-value-${key}`}>
                  {displayVal}
                </span>
              </div>
              <Slider
                data-testid={`proximity-slider-${key}`}
                value={[local[key]]}
                onValueChange={(val) => handleChange(key, val)}
                onValueCommit={(val) => handleCommit(key, val)}
                min={isEcaution ? 0.1 : 1}
                max={isEcaution ? 2 : 50}
                step={isEcaution ? 0.1 : 1}
                className="w-full"
              />
            </div>
          );
        })}
        <p className="text-[10px] text-gray-400 font-body leading-tight mt-1">
          {t("proximity.help")}
        </p>

        {/* Voice language for ALL spoken alerts (emergency + eCaution) */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Volume2 className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs uppercase tracking-widest font-bold text-gray-500">{t("voice_lang.title")}</span>
          </div>
          <select
            value={ttsLang}
            onChange={(e) => {
              setTtsLang(e.target.value);
              setPreferredTtsLang(e.target.value);
            }}
            className="w-full rounded-sm border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 text-sm px-2 py-1.5 font-body bg-white"
            data-testid="tts-lang-select"
          >
            {TTS_LANGS.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => playSpokenAlert("fire", "Fire at Mile 2 market, move away from the area.", 450)}
            className="mt-1.5 text-[11px] text-amber-700 hover:text-amber-900 underline font-body"
            data-testid="tts-test-btn"
          >
            ▶ Preview a full alert
          </button>
          <p className="text-[10px] text-gray-400 font-body leading-tight mt-1">
            Voices every new alert (security, fire, medical, accident, banditry, caution)
            in your chosen language — full description + distance. Local voices fall back
            to English if your device doesn&apos;t ship them.
          </p>
        </div>

        {/* Push-notification self-test. Independent of the voice/proximity
            settings — helpful when support is diagnosing "I never got a
            ring even though I enabled it" cases. Uses POST /api/push/test
            which dispatches through the same code path as real doorbell
            rings, so any breakage (dead subscription, blocked push
            provider) surfaces the same way. */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Bell className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs uppercase tracking-widest font-bold text-gray-500">
              Push notifications
            </span>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 font-body mb-2">
            Status: <span className="font-bold text-gray-800 dark:text-gray-200" data-testid="push-permission-status">{pushPermission}</span>
          </p>
          <button
            type="button"
            onClick={handleSendTestPush}
            disabled={pushTestBusy}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-sm bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 text-xs font-bold font-body disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="push-test-btn"
          >
            {pushTestBusy ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Bell className="w-3.5 h-3.5" />
            )}
            Send test notification
          </button>
          <p className="text-[10px] text-gray-400 font-body leading-tight mt-1">
            Sends a real push through the same pipeline as Scan2Call rings.
            If it doesn&apos;t arrive on your device within a few seconds,
            check your OS notification settings and the browser&apos;s site
            permissions for Guard.
          </p>
        </div>
      </div>
    </div>
  );
}
